"use client";

import { useMemo, useState } from "react";

import { SearchIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { cn } from "@/lib/utils";

type ClientType = "Particulier" | "Professionnel";

type ClientSearchItem = {
  id: string;
  firstName: string;
  lastName: string;
  type: ClientType;
  siret?: string;
  birthDate?: string;
  phone?: string;
  email?: string;
  addressLine1?: string;
  postalCode?: string;
  city?: string;
};

const mockClients: ClientSearchItem[] = [
  {
    id: "client-1",
    firstName: "Jean",
    lastName: "Dupont",
    type: "Particulier",
    siret: "12345678901234",
    birthDate: "1985-03-15",
    phone: "+33 1 23 45 67 89",
    email: "jean.dupont@email.com",
    addressLine1: "123 Rue de la Paix",
    postalCode: "75001",
    city: "Paris",
  },
  {
    id: "client-2",
    firstName: "Marie",
    lastName: "Martin",
    type: "Particulier",
    siret: "98765432109876",
    birthDate: "1978-11-22",
    phone: "+33 4 56 78 90 12",
    email: "marie.martin@email.com",
    addressLine1: "456 Avenue des Champs",
    postalCode: "69000",
    city: "Lyon",
  },
];

function ClientSearchExisting({
  onSelect,
  selectedClientId,
}: {
  onSelect?: (client: ClientSearchItem) => void;
  selectedClientId?: string | null;
}) {
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mockClients;

    return mockClients.filter((client) => {
      const haystack = [
        client.firstName,
        client.lastName,
        client.email,
        client.siret,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [query]);

  return (
    <div className="space-y-4">
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <SearchIcon />
        </InputGroupAddon>
        <InputGroupInput
          placeholder="Rechercher par nom, SIRET ou email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton variant="secondary">Rechercher</InputGroupButton>
        </InputGroupAddon>
      </InputGroup>

      <div className="space-y-4">
        {results.map((client) => (
          <ClientResultCard
            key={client.id}
            client={client}
            isSelected={selectedClientId === client.id}
            onClick={() => onSelect?.(client)}
          />
        ))}
      </div>
    </div>
  );
}

function ClientResultCard({
  client,
  isSelected,
  onClick,
}: {
  client: ClientSearchItem;
  isSelected: boolean;
  onClick?: () => void;
}) {
  const fullName = `${client.firstName} ${client.lastName}`;
  const address = [client.addressLine1, client.postalCode, client.city]
    .filter(Boolean)
    .join(", ");

  return (
    <Card
      className={cn(
        "cursor-pointer transition-colors",
        isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="font-semibold">{fullName}</span>
            <Badge variant="secondary">{client.type}</Badge>
          </div>
          {client.birthDate ? (
            <span className="text-sm text-gray-600">
              Né(e) le : {client.birthDate}
            </span>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="space-y-1 text-sm text-gray-700">
            {client.siret ? <div>SIRET: {client.siret}</div> : null}
            {address ? <div>{address}</div> : null}
            {client.email ? <div>{client.email}</div> : null}
          </div>

          <div className="space-y-1 text-sm text-gray-700 md:text-right">
            {client.phone ? <div>{client.phone}</div> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export type { ClientSearchItem };
export { ClientSearchExisting };
